import { useEffect, useRef, useState } from 'react'

/**
 * Drives a loader for a request whose progress can't be observed (a JSON query).
 *
 * - Nothing shows for the first 160 ms, so a fast or cached load never flashes a loader.
 * - While loading, progress eases toward 85% and never claims to be done on its own.
 * - When loading ends the bar fills to 100% and holds for a beat before it is released, so the
 *   wordmark visibly completes instead of vanishing mid-fill.
 */
export function useLoadingGate(loading: boolean, startedAt?: number): { show: boolean; progress: number } {
  // Continuing a load that is already on screen: start visible, at the progress it had reached,
  // so the hand-over from the previous loader has no blank frame and no jump back.
  const continuing = () => loading && startedAt != null && performance.now() - startedAt >= 160
  const [show, setShow] = useState(continuing)
  const [progress, setProgress] = useState(() =>
    continuing() ? 0.85 * (1 - Math.exp(-(performance.now() - startedAt!) / 1800)) : 0)
  const started = useRef(0)

  useEffect(() => {
    if (!loading) return
    // `startedAt` continues a load that an earlier loader began (a lazy route's chunk, then its
    // data), so the fill carries on instead of restarting at 0%.
    started.current = startedAt ?? performance.now()
    const elapsed = performance.now() - started.current
    setProgress(0.85 * (1 - Math.exp(-elapsed / 1800)))
    const delay = window.setTimeout(() => setShow(true), Math.max(0, 160 - elapsed))
    const tick = window.setInterval(() => {
      setProgress(0.85 * (1 - Math.exp(-(performance.now() - started.current) / 1800)))
    }, 80)
    return () => { window.clearTimeout(delay); window.clearInterval(tick) }
  }, [loading, startedAt])

  useEffect(() => {
    if (loading || !show) return
    setProgress(1)
    const t = window.setTimeout(() => setShow(false), 380)
    return () => window.clearTimeout(t)
  }, [loading, show])

  return { show, progress }
}
